import React, { useState, useEffect } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";

import socket from "./socket";
import LanguageSwitcher from "./components/LanguageSwitcher";
import styles from "./Login.module.css";

const PAGE_BG = `${process.env.PUBLIC_URL}/fon.png`;
const HEADER_BG = `${process.env.PUBLIC_URL}/headerShapka.png`;

const Login = ({
  onLogin,
  onSetBalance,
  setUserBalance,
  userBalance,
}) => {
  const { t } = useTranslation();



  useEffect(() => {
  let viewport = document.querySelector('meta[name="viewport"]');
  const oldContent = viewport?.getAttribute("content") || null;
  const created = !viewport;

  if (!viewport) {
    viewport = document.createElement("meta");
    viewport.setAttribute("name", "viewport");
    document.head.appendChild(viewport);
  }

  viewport.setAttribute(
    "content",
    "width=device-width, initial-scale=1, viewport-fit=cover"
  );

  return () => {
    if (created) {
      viewport.remove();
    } else if (oldContent !== null) {
      viewport.setAttribute("content", oldContent);
    }
  };
}, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    socket.on("userBalance", ({ balance }) => {
      setUserBalance(balance);

      if (onSetBalance) {
        onSetBalance(balance);
      }
    });

    return () => {
      socket.off("userBalance");
    };
  }, [onSetBalance, setUserBalance]);

  useEffect(() => {
    console.log("userBalance:", userBalance);
  }, [userBalance]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        {
          email,
          password,
        }
      );

      const { token, username, role } = response.data;

      localStorage.setItem("token", token);
      localStorage.setItem(
        "auth:bump",
        String(Date.now())
      );

      axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${token}`;

      onLogin(token, username, role);

      socket.emit("getUserBalance", {
        username,
      });

      if (role === "superadmin") {
        navigate("/admin", {
          replace: true,
        });
      } else {
        navigate("/", {
          replace: true,
        });
      }

      setTimeout(() => {
        navigate(0);
      }, 0);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        t("auth.errors.login");

      setError(msg);

      console.error(
        "Error during login:",
        err?.response
          ? err.response.data
          : err.message
      );
    }
  };

  return (
    <div
      className={styles.page}
      style={{
        backgroundImage: `
          radial-gradient(
            900px 520px at 72% 12%,
            rgba(202,162,79,.08),
            transparent 60%
          ),
          radial-gradient(
            780px 520px at 20% 85%,
            rgba(79,167,230,.08),
            transparent 60%
          ),
          url(${PAGE_BG})
        `,
        backgroundRepeat:
          "no-repeat, no-repeat, no-repeat",
        backgroundSize:
          "auto, auto, cover",
        backgroundPosition:
          "center top, center bottom, center top",
      }}
    >
      <div
        className={styles.authAnimatedBg}
        aria-hidden="true"
      />

      <header
        className={styles.header}
        style={{
          backgroundImage: `url(${HEADER_BG})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className={styles.headerInner}>
          <NavLink
            to="/"
            className={styles.logo}
          >
            <span className={styles.logoMain}>
              BACKGAMMON
            </span>

            <span className={styles.logoAccent}>
              QUEST
            </span>
          </NavLink>

          <nav
            className={styles.menu}
            aria-label={t(
              "auth.mainMenuAria"
            )}
          >
            <NavLink
              to="/nardy"
              className={({ isActive }) =>
                `${styles.menuItem} ${
                  isActive
                    ? styles.menuItemActive
                    : ""
                }`
              }
            >
              {t("header.backgammon")}
            </NavLink>

            <NavLink
              to="/quests"
              className={({ isActive }) =>
                `${styles.menuItem} ${
                  isActive
                    ? styles.menuItemActive
                    : ""
                }`
              }
            >
              {t("header.quests")}
            </NavLink>

            <NavLink
              to="/poker"
              className={({ isActive }) =>
                `${styles.menuItem} ${
                  isActive
                    ? styles.menuItemActive
                    : ""
                }`
              }
            >
              {t("header.poker")}
            </NavLink>

            <NavLink
              to="/satellites"
              className={({ isActive }) =>
                `${styles.menuItem} ${
                  isActive
                    ? styles.menuItemActive
                    : ""
                }`
              }
            >
              {t("header.satellites")}
            </NavLink>
          </nav>

          <div
            className={styles.headerButtons}
          >
            <div
              className={styles.languageSlot}
            >
              <LanguageSwitcher />
            </div>

            <button
              type="button"
              className={styles.loginButton}
              onClick={() =>
                navigate("/login")
              }
            >
              {t("header.login")}
            </button>

            <button
              type="button"
              className={
                styles.registerButton
              }
              onClick={() =>
                navigate("/register")
              }
            >
              {t("header.register")}
            </button>
          </div>
        </div>
      </header>

      <main className={styles.authPage}>
        <div className={styles.card}>
          <div className={styles.brandBar}>
            BACKGAMMON QUEST
          </div>

          <form
            className={styles.form}
            onSubmit={handleSubmit}
          >
            <h2 className={styles.title}>
              {t("auth.loginTitle")}
            </h2>

            <div className={styles.group}>
              <label htmlFor="login-email">
                {t("auth.email")}
              </label>

              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) =>
                  setEmail(e.target.value)
                }
                autoComplete="email"
                placeholder={t(
                  "auth.emailPlaceholder"
                )}
              />
            </div>

            <div className={styles.group}>
              <label htmlFor="login-password">
                {t("auth.password")}
              </label>

              <input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) =>
                  setPassword(
                    e.target.value
                  )
                }
                autoComplete="current-password"
                placeholder={t(
                  "auth.loginPasswordPlaceholder"
                )}
              />
            </div>

            {error ? (
              <div className={styles.error}>
                {error}
              </div>
            ) : null}

            <div className={styles.actions}>
              <button
                type="submit"
                className={styles.primary}
              >
                {t("auth.signIn")}
              </button>

              <button
                type="button"
                className={styles.secondary}
                onClick={() =>
                  navigate("/register")
                }
              >
                {t("auth.register")}
              </button>
            </div>

            <div className={styles.subLinks}>
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() =>
                  navigate(
                    "/forgot-password"
                  )
                }
              >
                {t(
                  "auth.forgotPassword"
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default Login;
